// Import necessary dependencies for React hooks, API, and components
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests
import EmployeeAddModal from './modals/Employee/EmployeeAddModal'; // Modal component for adding/editing employees
import Pagination from '../Tools/Pagination'; // Component for pagination controls

// EmployeeTab component for managing EMS employees
const EmployeeTab = () => {
  // State management for employee data and UI
  const [employees, setEmployees] = useState([]); // Stores the list of employees
  const [showModal, setShowModal] = useState(false); // Controls visibility of add/edit modal
  const [formData, setFormData] = useState({
    emp_id: '', // Employee ID
    first_name: '', // First name
    last_name: '', // Last name
    speciality: '', // Employee speciality
    email: '', // Employee email
    phone_number: '', // Employee phone number
    available_status: 1, // Default to available status
    case_assigned: '', // Assigned case ID
    location_id: '', // Location ID
    area_id: '', // Area ID
    medical_center_id: '' // Medical center ID
  }); // Form data for employee creation/editing
  const [isEditMode, setIsEditMode] = useState(false); // Tracks if modal is in edit mode
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null); // ID of employee being edited
  const [filterForm, setFilterForm] = useState({
    emp_id: '', // Filter by employee ID
    first_name: '', // Filter by first name
    speciality: '' // Filter by speciality
  }); // Filter criteria for employees
  const [page, setPage] = useState(1); // Current page for pagination
  const limit = 10; // Number of items per page

  /**
   * Displays an alert message to the user
   * @param {string} message - The message to display
   * @param {boolean} isSuccess - Whether it's a success message (default true)
   */
  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  /**
   * Fetches employees from the API based on current filters, role, and user_id
   * Only fetches employees with Role_Name 'EMS Resource Manager'
   * Includes user_id in params if Role_Name is 'EMS Resource Manager', else sends empty string
   */
  const fetchEmployees = async () => {
    setIsLoading(true); // Set loading state to true
    try {
      // Retrieve role and user ID from sessionStorage
      const roleName = sessionStorage.getItem('Role_Name');
      const userId = sessionStorage.getItem('user_id') || '';
      // Define query parameters for API request
      const params = {
        emp_id: filterForm.emp_id,
        first_name: filterForm.first_name,
        speciality: filterForm.speciality,
        role_name: 'EMS Resource Manager', // Filter by role
        user_id: roleName === 'EMS Resource Manager' ? userId : '' // Conditional user_id
      };

      // Make GET request to fetch employees
      const response = await axiosInstance.get('/ems/employees', { params });

      // Handle different response formats (array or object with data property)
      const data = response.data?.data || response.data || [];
      setEmployees(data); // Update employees state
    } catch (error) {
      // Log error and show alert on failure
      console.error('Error fetching employees:', error);
      showAlert('Failed to fetch employees', false);
      setEmployees([]); // Reset employees on error
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  // Fetch employees when component mounts or filters change
  useEffect(() => {
    fetchEmployees();
  }, [filterForm]); // Re-run when filterForm changes

  /**
   * Handles input changes for the employee form
   * @param {Object} e - The event object
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target; // Extract name and value from input
    setFormData(prev => ({ ...prev, [name]: value })); // Update form data
  };

  /**
   * Handles changes to filter inputs
   * @param {Object} e - The event object
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target; // Extract name and value from filter input
    setFilterForm(prev => ({ ...prev, [name]: value })); // Update filter form
  };

  /**
   * Resets all filter fields to their default values
   */
  const clearFilters = () => {
    setFilterForm({
      emp_id: '',
      first_name: '',
      speciality: ''
    }); // Reset filter fields
  };

  /**
   * Opens the modal in "add new employee" mode
   * Resets form data to default values
   */
  const openAddModal = () => {
    setIsEditMode(false); // Set to add mode
    setFormData({
      emp_id: '',
      first_name: '',
      last_name: '',
      speciality: '',
      email: '',
      phone_number: '',
      available_status: 1,
      case_assigned: '',
      location_id: '',
      area_id: '',
      medical_center_id: ''
    }); // Reset form data
    setShowModal(true); // Show modal
  };

  /**
   * Prepares the modal for editing an existing employee
   * @param {Object} employee - The employee data to edit
   */
  const handleEdit = (employee) => {
    setIsEditMode(true); // Set to edit mode
    setCurrentEmployeeId(employee.id); // Set employee ID

    // Store location and area IDs in session for reference
    if (employee.area_id) {
      sessionStorage.setItem('area_id', employee.area_id);
    }
    if (employee.location_id) {
      sessionStorage.setItem('location_id', employee.location_id);
    }

    // Populate form with employee data
    setFormData({
      emp_id: employee.emp_id,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone_number: employee.phone_number,
      available_status: employee.available_status,
      case_assigned: employee.case_assigned || '',
      location_id: employee.location_id || '',
      area_id: employee.area_id || '',
      speciality: employee.speciality?.id || employee.speciality || '',
      medical_center_id: employee.medical_center_id || ''
    });

    setShowModal(true); // Show modal
  };

  /**
   * Handles form submission for both adding and editing employees
   * Includes user_id from session in the API payload
   */
  const handleSave = async () => {
    try {
      setIsLoading(true); // Set loading state
      const token = sessionStorage.getItem('auth_token'); // Get auth token
      const userId = sessionStorage.getItem('user_id'); // Get user_id from session
      const payload = new URLSearchParams();

      // Convert form data to URL-encoded format
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value || '');
      });
      // Add user_id to payload
      payload.append('user_id', userId || '');

      // Log payload for debugging
      console.log('payload:', payload.toString());

      if (isEditMode) {
        // Update existing employee
        await axiosInstance.put(`/ems/employees/${currentEmployeeId}`, payload.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
        });
        showAlert('Employee updated successfully!');
      } else {
        // Create new employee
        await axiosInstance.post('/ems/employees', payload.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
        });
        showAlert('Employee added successfully!');
      }

      setShowModal(false); // Close modal
      fetchEmployees(); // Refresh the employee list
    } catch (error) {
      console.error('Save failed:', error);

      // Extract message safely
      const errorMsg =
        error?.response?.data?.errors?.employee_id?.[0] || // specific: employee_id already exists
        error?.response?.data?.message ||                  // generic backend message
        `Failed to ${isEditMode ? 'update' : 'add'} employee`; // fallback

      showAlert(errorMsg, false);
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  /**
   * Deletes an employee after confirmation
   * @param {number} id - The ID of the employee to delete
   */
  const handleDelete = async (id) => {
    // Confirm deletion with user
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        setIsLoading(true); // Set loading state
        const token = sessionStorage.getItem('auth_token'); // Get auth token
        // Make DELETE request to remove employee
        await axiosInstance.delete(`/ems/employees/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        showAlert('Employee deleted successfully!');
        fetchEmployees(); // Refresh the employee list
      } catch (error) {
        // Log error and show alert on failure
        console.error('Delete failed:', error);
        showAlert('Failed to delete employee', false);
      } finally {
        setIsLoading(false); // Clear loading state
      }
    }
  };

  /**
   * Handles modal close and cleans up session storage
   */
  const handleModalClose = () => {
    setShowModal(false); // Close modal
    // Clear temporary session storage
    sessionStorage.removeItem('location_id');
    sessionStorage.removeItem('area_id');
  };

  // Pagination calculations
  const currentItems = employees.slice((page - 1) * limit, page * limit); // Items for current page
  const total = employees.length; // Total number of employees

  // Render the component
  return (
    <div className="tab-pane active" id="employee" role="tabpanel">
      {/* Filter Section */}
      <div className="filter-search mt-5">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input
                type="text"
                className="form-control"
                name="emp_id"
                placeholder="Employee ID"
                value={filterForm.emp_id}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input
                type="text"
                className="form-control"
                name="first_name"
                placeholder="First Name"
                value={filterForm.first_name}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group col-lg-3 p-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-rounded"
                onClick={clearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>
          <button
            className="btn my-2 d-flex align-items-center justify-content-center btn-outline-primary"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Employee
          </button>
        </div>
      </div>

      {/* Employee Table */}
      {isLoading ? (
        <div className="text-center py-4">Loading employees...</div>
      ) : (
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle">
              <thead className="text-dark fs-4">
                <tr>
                  <th><h6 className="fw-semibold mb-0">ID</h6></th>
                  <th><h6 className="fw-semibold mb-0">Name</h6></th>
                  <th><h6 className="fw-semibold mb-0">Email</h6></th>
                  <th><h6 className="fw-semibold mb-0">Phone</h6></th>
                  <th><h6 className="fw-semibold mb-0">Speciality</h6></th>
                  <th><h6 className="fw-semibold mb-0">Location</h6></th>
                  <th><h6 className="fw-semibold mb-0">Medical Center</h6></th>
                  <th><h6 className="fw-semibold mb-0">Status</h6></th>
                  <th><h6 className="fw-semibold mb-0">Action</h6></th>
                </tr>
              </thead>
              <tbody>
                {employees.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      No Employees Found
                    </td>
                  </tr>
                ) : (
                  currentItems.map((emp, index) => (
                    <tr key={emp.id}>
                      <td>{emp.emp_id}</td>
                      <td>{emp.first_name} {emp.last_name}</td>
                      <td>{emp.email}</td>
                      <td>{emp.phone_number}</td>
                      <td>{emp.speciality || 'N/A'}</td>
                      <td>{emp.ems_location?.location_name || 'N/A'}</td>
                      <td>{emp.medical_center?.name || 'N/A'}</td>
                      <td>
                        <span className={`round-8 rounded-circle me-2 d-inline-block ${emp.available_status ? 'text-bg-success' : 'text-bg-danger'}`}></span>
                        {emp.available_status ? 'Available' : 'Busy'}
                      </td>
                      <td>
                        <button
                          className="h4 me-3 border-0 bg-transparent"
                          onClick={() => handleEdit(emp)}
                          aria-label="Edit employee"
                        >
                          <i className="ti ti-edit text-warning"></i>
                        </button>
                        <button
                          className="h4 border-0 bg-transparent"
                          onClick={() => handleDelete(emp.id)}
                          aria-label="Delete employee"
                        >
                          <i className="ti ti-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination - only shown when there are employees */}
          {employees.length > 0 && (
            <Pagination
              total={total}
              limit={limit}
              page={page}
              onPageChange={setPage}
            />
          )}

          {/* Add/Edit Employee Modal */}
          <EmployeeAddModal
            show={showModal}
            onClose={handleModalClose}
            formData={formData}
            onChange={handleInputChange}
            onSave={handleSave}
            isEditMode={isEditMode}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

// Export the component
export default EmployeeTab;